/**
 * 表格增强：列宽拖拽 + 省略内容悬浮放大提示
 * 适配 ant-design-vue 1.x（无原生 resizeColumn）
 * 原则：只改当前列宽；表宽 = 列宽之和；超出则横向滚动
 */

import { scheduleTableFixedAlign } from "@/utils/sync-table-fixed-rows"

const MIN_COL_WIDTH = 48
const SELECTION_COL_WIDTH = 60
const TIP_CLASS = "erp-cell-overflow-tip"
const HANDLE_CLASS = "erp-col-resize"
const RESIZING_CLASS = "erp-col-resizing"

let tipEl = null
let tipAnchor = null
let tipInstalled = false

function getTipEl() {
  if (tipEl) return tipEl
  tipEl = document.createElement("div")
  tipEl.className = TIP_CLASS
  tipEl.style.display = "none"
  document.body.appendChild(tipEl)
  return tipEl
}

function hideOverflowTip() {
  tipAnchor = null
  if (tipEl) tipEl.style.display = "none"
}

function positionTip(anchorRect) {
  const el = getTipEl()
  const pad = 8
  const maxW = Math.min(560, window.innerWidth - pad * 2)
  el.style.maxWidth = `${maxW}px`
  el.style.display = "block"
  el.style.left = "0px"
  el.style.top = "0px"

  const rect = el.getBoundingClientRect()
  let left = anchorRect.left
  let top = anchorRect.bottom + 6
  if (left + rect.width > window.innerWidth - pad) {
    left = Math.max(pad, window.innerWidth - rect.width - pad)
  }
  if (top + rect.height > window.innerHeight - pad) {
    top = Math.max(pad, anchorRect.top - rect.height - 6)
  }
  el.style.left = `${Math.max(pad, left)}px`
  el.style.top = `${top}px`
}

function showOverflowTip(text, anchorEl) {
  const content = String(text || "").trim()
  if (!content) return
  tipAnchor = anchorEl
  const el = getTipEl()
  el.textContent = content
  positionTip(anchorEl.getBoundingClientRect())
}

function isInteractiveHeavyCell(td) {
  if (!td) return true
  if (td.classList.contains("ant-table-selection-column")) return true
  if (td.classList.contains("erp-table-operation-col")) return true
  if (
    td.querySelector(".ant-btn, button, input, select, textarea, .ant-upload")
  ) {
    return true
  }
  return false
}

function isOverflowEl(el) {
  if (!el) return false
  return el.scrollWidth > el.clientWidth + 1
}

function isCellOverflow(td) {
  if (!td) return false
  if (isOverflowEl(td)) return true
  const candidates = td.querySelectorAll("a, span, div, p")
  for (let i = 0; i < candidates.length; i++) {
    const node = candidates[i]
    if (node.closest(".el-tag, .ant-tag, .ant-btn, button")) continue
    if (isOverflowEl(node)) return true
  }
  return false
}

function stripNativeTitle(td) {
  if (!td) return
  if (td.hasAttribute("title")) {
    td.setAttribute("data-erp-native-title", td.getAttribute("title") || "")
    td.removeAttribute("title")
  }
  const titled = td.querySelectorAll("[title]")
  for (let i = 0; i < titled.length; i++) {
    const node = titled[i]
    if (!node.hasAttribute("data-erp-native-title")) {
      node.setAttribute(
        "data-erp-native-title",
        node.getAttribute("title") || ""
      )
    }
    node.removeAttribute("title")
  }
}

/**
 * 全局安装省略内容悬浮提示（全站表格生效）
 */
export function installOverflowTooltip() {
  if (typeof document === "undefined" || tipInstalled) return
  tipInstalled = true

  document.addEventListener(
    "mouseover",
    e => {
      const td =
        e.target && e.target.closest
          ? e.target.closest(".ant-table-tbody > tr > td")
          : null
      if (!td || isInteractiveHeavyCell(td)) return
      if (!isCellOverflow(td)) {
        if (tipAnchor === td) hideOverflowTip()
        return
      }
      stripNativeTitle(td)
      const text = (td.innerText || td.textContent || "").replace(/\s+/g, " ")
      showOverflowTip(text, td)
    },
    true
  )

  document.addEventListener(
    "mouseout",
    e => {
      const td =
        e.target && e.target.closest
          ? e.target.closest(".ant-table-tbody > tr > td")
          : null
      if (!td) return
      if (e.relatedTarget && td.contains(e.relatedTarget)) return
      if (tipAnchor === td) hideOverflowTip()
    },
    true
  )

  document.addEventListener(
    "scroll",
    () => {
      if (tipAnchor) hideOverflowTip()
    },
    true
  )

  window.addEventListener("resize", () => {
    if (tipAnchor) hideOverflowTip()
  })
}

function findHeaderRows(tableRoot) {
  return Array.prototype.slice.call(
    tableRoot.querySelectorAll(".ant-table-thead > tr")
  )
}

function isSkippableTh(th) {
  if (!th) return true
  if (th.classList.contains("ant-table-selection-column")) return true
  if (th.classList.contains("ant-table-expand-icon-th")) return true
  if (th.classList.contains("ant-table-row-expand-icon-cell")) return true
  return false
}

function getColumnsList(columnsOrGetter) {
  if (typeof columnsOrGetter === "function") {
    return columnsOrGetter() || []
  }
  return columnsOrGetter || []
}

function getColumnsForHeaderRow(tr, allColumns) {
  const cols = allColumns || []
  if (tr.closest(".ant-table-fixed-left")) {
    return cols.filter(c => c.fixed === "left" || c.fixed === true)
  }
  if (tr.closest(".ant-table-fixed-right")) {
    return cols.filter(c => c.fixed === "right")
  }
  return cols
}

function mapThToColumns(tr, columns) {
  const ths = Array.prototype.slice.call(tr.children || [])
  const mapped = []
  let colIndex = 0
  ths.forEach((th, thIndex) => {
    if (isSkippableTh(th)) {
      mapped.push({ th, thIndex, col: null })
      return
    }
    const col = columns[colIndex++] || null
    mapped.push({ th, thIndex, col })
  })
  return mapped
}

function isFixedRightCol(col, th) {
  if (col && col.fixed === "right") return true
  if (th && th.closest(".ant-table-fixed-right")) return true
  if (
    col &&
    (col.key === "operation" ||
      (col.className &&
        String(col.className).includes("erp-table-operation-col")))
  ) {
    // 操作列即使未 fixed，也优先左侧拖拽，避免贴边难拖
    return true
  }
  return false
}

function setColWidthOnGroup(group, absThIndex, width) {
  const col = group && group.children[absThIndex]
  if (col) {
    col.style.width = `${width}px`
    col.style.minWidth = `${width}px`
  }
}

function getColPixelWidth(col) {
  if (!col) return 120
  if (col._dragWidth != null) {
    return Math.max(MIN_COL_WIDTH, Number(col._dragWidth) || MIN_COL_WIDTH)
  }
  if (typeof col.width === "number" && col.width > 0) return col.width
  if (typeof col.width === "string") {
    const n = parseInt(col.width, 10)
    if (!Number.isNaN(n) && n > 0) return n
  }
  return 120
}

/**
 * 从主表头读取当前各列像素宽，写回 columns，锁定后只改一列不会牵动其它列
 */
function snapshotColumnWidthsFromDom(tableRoot, columns) {
  const scroll =
    tableRoot.querySelector(".ant-table-scroll") ||
    tableRoot.querySelector(".ant-table-content") ||
    tableRoot
  const tr = scroll.querySelector(".ant-table-thead > tr")
  if (!tr) return {}
  const mapped = mapThToColumns(tr, columns)
  const map = {}
  mapped.forEach(({ th, col }) => {
    if (!th || !col || !col._colId) return
    const w = Math.round(
      th.getBoundingClientRect().width || th.offsetWidth || 0
    )
    if (w > 0) {
      map[col._colId] = w
      col.width = w
    } else if (typeof col.width !== "number" || col.width <= 0) {
      col.width = 120
      map[col._colId] = 120
    } else {
      map[col._colId] = col.width
    }
  })
  // 主表头未出现的列（极端情况）也保证有数字宽
  ;(columns || []).forEach(col => {
    if (!col || !col._colId) return
    if (map[col._colId] == null) {
      const w = getColPixelWidth(col)
      col.width = w
      map[col._colId] = w
    }
  })
  return map
}

/**
 * 按「列宽之和」设置主表宽度，并写回每一列的 col/th 宽
 * 这样只改一列时，其它列保持原像素宽，超出容器则出现横向滚动
 */
function applyIndependentColumnWidths(
  tableRoot,
  columns,
  activeColId,
  activeWidth
) {
  const scroll =
    tableRoot.querySelector(".ant-table-scroll") ||
    tableRoot.querySelector(".ant-table-content") ||
    tableRoot
  if (!scroll) return 0

  const widthOf = col => {
    if (!col) return 120
    if (activeColId && col._colId === activeColId && activeWidth != null) {
      return Math.max(MIN_COL_WIDTH, activeWidth)
    }
    return getColPixelWidth(col)
  }

  const dataSum = (columns || []).reduce((s, c) => s + widthOf(c), 0)
  const tr = scroll.querySelector(".ant-table-thead > tr")
  const hasSelection =
    tr &&
    Array.prototype.some.call(tr.children || [], th =>
      th.classList.contains("ant-table-selection-column")
    )
  const total = dataSum + (hasSelection ? SELECTION_COL_WIDTH : 0)

  // 主滚动区：表宽 = 列宽总和，避免 100% 布局重分配
  scroll.querySelectorAll("table").forEach(table => {
    table.style.width = `${total}px`
    table.style.minWidth = `${total}px`
    table.style.tableLayout = "fixed"
  })

  if (tr) {
    const mapped = mapThToColumns(tr, columns)
    scroll.querySelectorAll("colgroup").forEach(group => {
      mapped.forEach(({ th, thIndex, col }) => {
        if (isSkippableTh(th)) {
          if (th.classList.contains("ant-table-selection-column")) {
            setColWidthOnGroup(group, thIndex, SELECTION_COL_WIDTH)
          }
          return
        }
        if (!col) return
        const w = widthOf(col)
        setColWidthOnGroup(group, thIndex, w)
      })
    })
    mapped.forEach(({ th, col }) => {
      if (!th || !col) return
      const w = widthOf(col)
      th.style.width = `${w}px`
      th.style.minWidth = `${w}px`
      th.style.maxWidth = `${w}px`
    })
  }

  // 固定左/右列各自独立写宽，不改其它区域
  ;["left", "right"].forEach(side => {
    const panel = tableRoot.querySelector(`.ant-table-fixed-${side}`)
    if (!panel) return
    const ptr = panel.querySelector(".ant-table-thead > tr")
    if (!ptr) return
    const headerCols = getColumnsForHeaderRow(ptr, columns)
    // rowSelection 会单独生成 fixed-left，即使业务列没有任何左固定列。
    // 这种面板完全交给 antd 管理，不能按空业务列集合重写其 table 宽度。
    if (!headerCols.length) return
    const mapped = mapThToColumns(ptr, headerCols)
    let panelSum = 0
    mapped.forEach(({ th, col }) => {
      if (col) {
        panelSum += widthOf(col)
      } else if (th && th.classList.contains("ant-table-selection-column")) {
        panelSum += SELECTION_COL_WIDTH
      }
    })
    if (panelSum <= 0) return
    panel.querySelectorAll("table").forEach(table => {
      table.style.width = `${panelSum}px`
      table.style.minWidth = `${panelSum}px`
      table.style.tableLayout = "fixed"
    })
    panel.querySelectorAll("colgroup").forEach(group => {
      mapped.forEach(({ thIndex, col }) => {
        if (!col) return
        setColWidthOnGroup(group, thIndex, widthOf(col))
      })
    })
    mapped.forEach(({ th, col }) => {
      if (!th || !col) return
      const w = widthOf(col)
      th.style.width = `${w}px`
      th.style.minWidth = `${w}px`
      th.style.maxWidth = `${w}px`
    })
    if (side === "right" && panelSum > 0) {
      panel.style.width = `${panelSum}px`
      const outer = panel.querySelector(".ant-table-body-outer")
      if (outer) outer.style.width = `${panelSum}px`
    }
  })

  return total
}

function ensureHandle(th, colId, options = {}) {
  if (!th) return null
  const fromLeft = !!options.fromLeft
  const sideClass = fromLeft
    ? `${HANDLE_CLASS}--left`
    : `${HANDLE_CLASS}--right`
  let handle = th.querySelector(`.${HANDLE_CLASS}`)
  if (handle) {
    handle.dataset.colId = colId || ""
    handle.dataset.fromLeft = fromLeft ? "1" : "0"
    handle.classList.remove(`${HANDLE_CLASS}--left`, `${HANDLE_CLASS}--right`)
    handle.classList.add(sideClass)
    return handle
  }
  if (typeof window.getComputedStyle === "function") {
    const pos = window.getComputedStyle(th).position
    if (!pos || pos === "static") {
      th.style.position = "relative"
    }
  } else {
    th.style.position = "relative"
  }
  handle = document.createElement("div")
  handle.className = `${HANDLE_CLASS} ${sideClass}`
  handle.dataset.colId = colId || ""
  handle.dataset.fromLeft = fromLeft ? "1" : "0"
  th.appendChild(handle)
  return handle
}

/**
 * 为表格根节点注入列宽拖拽手柄，并绑定拖拽逻辑
 * @param {Element} tableRoot
 * @param {Array|Function} columnsOrGetter 列数组或 getter（便于列替换后仍读到最新）
 * @param {Function} onResizeEnd
 * @returns {{ refresh: Function, destroy: Function }}
 */
export function bindColumnResize(tableRoot, columnsOrGetter, onResizeEnd) {
  if (!tableRoot) {
    return { refresh() {}, destroy() {} }
  }

  const state = {
    dragging: false,
    startX: 0,
    startW: 0,
    currentW: 0,
    absThIndex: -1,
    col: null,
    th: null,
    fromLeft: false
  }

  let syncTimer = null
  const scheduleSync = () => {
    if (syncTimer) clearTimeout(syncTimer)
    syncTimer = setTimeout(() => {
      syncTimer = null
      scheduleTableFixedAlign(tableRoot)
    }, 80)
  }

  const inject = () => {
    const columns = getColumnsList(columnsOrGetter)
    if (!columns.length) return
    findHeaderRows(tableRoot).forEach(tr => {
      const headerCols = getColumnsForHeaderRow(tr, columns)
      mapThToColumns(tr, headerCols).forEach(({ th, col }) => {
        if (!th || !col) return
        ensureHandle(th, col._colId, {
          fromLeft: isFixedRightCol(col, th)
        })
      })
    })
    // 按列宽之和设置表宽，保证横向滚动且各列互不挤压
    applyIndependentColumnWidths(tableRoot, columns)
    // 列宽变化会导致换行重排，需同步固定列行高
    scheduleSync()
  }

  const applyDragWidth = w => {
    if (!state.col) return
    state.col._dragWidth = w
    applyIndependentColumnWidths(
      tableRoot,
      getColumnsList(columnsOrGetter),
      state.col._colId,
      w
    )
    state.currentW = w
  }

  const onMove = e => {
    if (!state.dragging || !state.col) return
    const delta = e.clientX - state.startX
    // 左侧手柄：向左拖加宽，向右拖变窄
    const w = Math.max(
      MIN_COL_WIDTH,
      state.fromLeft ? state.startW - delta : state.startW + delta
    )
    applyDragWidth(w)
  }

  const onUp = () => {
    if (!state.dragging) return
    state.dragging = false
    document.body.classList.remove(RESIZING_CLASS)
    document.removeEventListener("mousemove", onMove)
    document.removeEventListener("mouseup", onUp)
    const w = state.currentW
    const col = state.col
    if (col) delete col._dragWidth
    state.col = null
    state.th = null
    state.fromLeft = false
    if (col && typeof w === "number" && !Number.isNaN(w)) {
      onResizeEnd && onResizeEnd(w, col)
    }
    scheduleTableFixedAlign(tableRoot)
  }

  const onDown = e => {
    const handle =
      e.target && e.target.closest ? e.target.closest(`.${HANDLE_CLASS}`) : null
    if (!handle || !tableRoot.contains(handle)) return
    const th = handle.parentElement
    if (!th) return
    const tr = th.parentElement
    if (!tr) return

    const columns = getColumnsList(columnsOrGetter)
    const headerCols = getColumnsForHeaderRow(tr, columns)
    const mapped = mapThToColumns(tr, headerCols)
    const hit = mapped.find(item => item.th === th)
    if (!hit || !hit.col) return

    e.preventDefault()
    e.stopPropagation()
    hideOverflowTip()

    // 拖拽前：把当前视觉宽度全部锁进 columns，确保只改本列
    snapshotColumnWidthsFromDom(tableRoot, columns)
    applyIndependentColumnWidths(tableRoot, columns)

    state.dragging = true
    state.fromLeft = handle.dataset.fromLeft === "1"
    state.startX = e.clientX
    state.startW =
      th.getBoundingClientRect().width ||
      th.offsetWidth ||
      hit.col.width ||
      MIN_COL_WIDTH
    state.currentW = state.startW
    state.absThIndex = hit.thIndex
    state.col = hit.col
    state.th = th
    document.body.classList.add(RESIZING_CLASS)
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  }

  inject()
  tableRoot.addEventListener("mousedown", onDown)

  let observer = null
  if (typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(() => {
      if (state.dragging) return
      inject()
    })
    observer.observe(tableRoot, { childList: true, subtree: true })
  }

  return {
    refresh: inject,
    destroy() {
      if (syncTimer) {
        clearTimeout(syncTimer)
        syncTimer = null
      }
      tableRoot.removeEventListener("mousedown", onDown)
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
      document.body.classList.remove(RESIZING_CLASS)
      if (observer) observer.disconnect()
      tableRoot.querySelectorAll(`.${HANDLE_CLASS}`).forEach(node => {
        if (node.parentNode) node.parentNode.removeChild(node)
      })
    }
  }
}

function collectTableRoots(container) {
  if (!container || !container.querySelectorAll) return []
  return Array.prototype.slice.call(container.querySelectorAll(".ant-table"))
}

/**
 * 增强 presenter 页内主列表表格（列宽拖拽 + 与 CRUD 持久化对接）
 * 可重复调用：已绑定的表只 refresh，新出现的表才新建绑定
 */
export function enhancePresenterTables(vm) {
  if (!vm || !vm.$el || !vm.crud) return () => {}

  if (!vm._erpResizeBinders) {
    vm._erpResizeBinders = new Map()
  }
  const binders = vm._erpResizeBinders
  const getColumns = () => vm.crud.columns || []

  // 优先主列表（.table-container 内），避免弹窗/嵌套表误绑到 crud.columns
  const preferred = Array.prototype.slice.call(
    vm.$el.querySelectorAll(".table-container .ant-table")
  )
  const roots = preferred.length ? preferred : collectTableRoots(vm.$el)
  const targets = roots.filter(root => root.querySelector(".ant-table-thead"))
  const alive = new Set()

  targets.forEach(root => {
    alive.add(root)
    if (binders.has(root)) {
      const binder = binders.get(root)
      binder && binder.refresh && binder.refresh()
      return
    }
    const binder = bindColumnResize(root, getColumns, (width, col) => {
      if (typeof vm.crud.resizeColumn === "function") {
        // 传入当前全部列宽快照，便于一次记住所有列（避免其它列被重分配后丢失）
        vm.crud.resizeColumn(width, col)
      } else {
        col.width = Math.max(MIN_COL_WIDTH, Math.round(width))
      }
      vm.$nextTick(() => {
        applyIndependentColumnWidths(root, getColumns())
        scheduleTableFixedAlign(root)
      })
    })
    binders.set(root, binder)
  })

  // 清理已卸载的表格根节点
  binders.forEach((binder, root) => {
    if (!alive.has(root)) {
      binder && binder.destroy && binder.destroy()
      binders.delete(root)
    }
  })

  return () => {
    binders.forEach(binder => binder && binder.destroy && binder.destroy())
    binders.clear()
  }
}
